<template>
	<v-menu
		v-model="menuOpen"
		:close-on-content-click="false"
		location="bottom end"
		max-width="420"
		min-width="380"
		transition="slide-y-transition"
	>
		<template #activator="{ props: menuProps }">
			<v-btn
				v-bind="menuProps"
				class="notification-bell-btn"
				variant="outlined"
				color="primary"
				size="default"
			>
				<v-badge
					v-if="unreadCount > 0"
					:content="unreadCount"
					color="accent"
					text-color="white"
					floating
				>
					<v-icon>mdi-bell-outline</v-icon>
				</v-badge>
				<v-icon v-else>mdi-bell-outline</v-icon>
			</v-btn>
		</template>

		<v-card width="350">
			<div class="d-flex align-center px-3 py-2 border-b">
				<span class="text-body-2 font-weight-medium">Notifications</span>
				<v-spacer />
				<v-btn
					v-if="unreadCount > 0"
					size="x-small"
					variant="text"
					color="secondary"
					@click="markAllRead"
				>
					Mark all read
				</v-btn>
			</div>

			<div style="max-height: 400px; overflow-y: auto">
				<div v-if="loading" class="d-flex justify-center pa-4">
					<v-progress-circular indeterminate size="24" color="secondary" />
				</div>

				<div
					v-else-if="notifications.length === 0"
					class="pa-4 text-center text-body-2 text-grey-darken-2"
				>
					No notifications
				</div>

				<div
					v-for="notification in notifications"
					:key="notification.uuid"
					class="px-3 py-2 notification-item cursor-pointer"
					:class="{ 'unread-item': !notification.read }"
					@click="handleClick(notification)"
				>
					<div class="d-flex align-center mb-1">
						<v-icon
							size="16"
							:color="getEventColor(notification.eventType)"
							class="mr-1"
						>
							{{ getEventIcon(notification.eventType) }}
						</v-icon>
						<span class="text-caption font-weight-medium">
							{{ getActorName(notification.actor) }}
						</span>
						<v-spacer />
						<span class="text-caption text-grey-darken-2">
							{{ formatTime(notification.createdAt) }}
						</span>
					</div>
					<div class="text-body-2">
						{{ getEventDescription(notification) }}
					</div>
					<div
						v-if="notification.paperName"
						class="text-caption text-grey-darken-1 text-truncate"
					>
						{{ notification.paperName }}
					</div>
				</div>
			</div>
		</v-card>
	</v-menu>
</template>

<script>
import axios from "axios";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import { WORKFLOW_STATES } from "@/constants/workflowStates";
import { personName } from "@/utils/person";

export default {
	name: "NotificationBell",

	data() {
		return {
			menuOpen: false,
			loading: false,
			notifications: [],
			unreadCount: 0,
			pollInterval: null,
		};
	},

	watch: {
		menuOpen(val) {
			if (val) {
				this.loadNotifications();
			}
		},
	},

	mounted() {
		this.fetchUnreadCount();
		this.pollInterval = setInterval(() => {
			this.fetchUnreadCount();
		}, 60000);
	},

	beforeUnmount() {
		if (this.pollInterval) {
			clearInterval(this.pollInterval);
		}
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		async fetchUnreadCount() {
			try {
				const res = await axios.get("/notifications/count");
				this.unreadCount = res.data.unreadCount || 0;
			} catch (err) {
				console.warn("Failed to fetch notification count:", err?.message);
			}
		},

		async loadNotifications() {
			this.loading = true;
			try {
				const res = await axios.get("/notifications");
				this.notifications = res.data.notifications;
				this.unreadCount = res.data.unreadCount || 0;
			} catch (err) {
				console.error("Failed to load notifications:", err);
			} finally {
				this.loading = false;
			}
		},

		async markAllRead() {
			try {
				await axios.put("/notifications/mark-all-read");
				this.unreadCount = 0;
				this.notifications = this.notifications.map((n) => ({
					...n,
					read: true,
				}));
			} catch (err) {
				console.error("Failed to mark all read:", err);
			}
		},

		handleClick(notification) {
			// Mark as read (fire-and-forget)
			if (!notification.read) {
				axios
					.put(`/notifications/${notification.uuid}/read`)
					.catch((err) =>
						console.warn("Notification action failed:", err?.message),
					);
				notification.read = true;
				this.unreadCount = Math.max(0, this.unreadCount - 1);
			}

			this.menuOpen = false;

			// DCTL-2327: Build the navigation target and only offer a candidate
			// that actually resolves to a route. Prefer the SDE deep link (it
			// already carries commentUuid), fall back to the QC wizard. A stale or
			// malformed sdeLink otherwise navigates to an unmatched route - there
			// is no catch-all, so the reviewer lands on a blank page with no way
			// back. `resolve().matched.length` is 0 for an unmatched path.
			const candidates = [];
			if (notification.sdeLink) {
				candidates.push(notification.sdeLink);
			}
			if (notification.qcUuid) {
				let qcTarget = `/quality-control/${notification.qcUuid}`;
				if (notification.commentUuid) {
					qcTarget += `?commentUuid=${notification.commentUuid}`;
				}
				candidates.push(qcTarget);
			}
			const target = candidates.find((path) => this.targetResolves(path));

			if (!target) {
				// Nothing to open, or every link is stale. Tell the reviewer
				// instead of silently doing nothing (the original symptom).
				this.showNotification({
					message:
						"Could not open that notification - the linked item may no longer be available.",
					status: "error",
				});
				return;
			}

			// A duplicate or guard-cancelled navigation resolves (no reject) in
			// Vue Router 4, so .catch fires only on a genuine failure - surface it
			// rather than swallowing it to the console as before.
			this.$router.push(target).catch((err) => {
				console.warn("Notification navigation failed:", err?.message);
				this.showNotification({
					message:
						"Could not open that notification. Reload and try again if it keeps happening.",
					status: "error",
				});
			});
		},

		// True when `path` maps to a real route. The router has no catch-all
		// (the /:pathMatch(.*)* fallback is commented out in router.js), so an
		// unmatched path resolves with an empty `matched` array.
		targetResolves(path) {
			try {
				return this.$router.resolve(path).matched.length > 0;
			} catch {
				return false;
			}
		},

		getActorName(actor) {
			return personName(actor) || "Someone";
		},

		// DCTL-2294: rework_request is the WORKFLOW axis and reads its glyph and
		// colour from the shared map, so a re-bucketing needs no edit here. It was
		// mdi-alert-circle-outline + "warning" - the same retired amber-as-rework the
		// QC board had already been made to drop, and the same glyph DCTL-2257
		// retired there. The other entries are genuine Vuetify roles and stay;
		// rework_resubmitted is a completion, not a rework.
		getEventIcon(eventType) {
			const icons = {
				rework_request: WORKFLOW_STATES.rework.icon,
				comment_reply: "mdi-reply",
				comment_directed: "mdi-comment-text-outline",
				rework_resubmitted: "mdi-check-circle-outline",
				comment_resolved: "mdi-check-decagram",
				// DCTL-2526. The mandated non-hue partner for the attention
				// colour below (COLOR.md rule 3), and distinct from rework's
				// mdi-arrow-u-left-top: a reopen is not a rejection.
				qc_reopened: "mdi-lock-open-variant-outline",
				// DCTL-2595. Same attention family as qc_reopened: an INPUT of
				// this QC was reopened, so what the reviewer is reading is
				// provisional until it is resubmitted.
				input_reopened: "mdi-lock-open-variant-outline",
			};
			return icons[eventType] || "mdi-bell-outline";
		},

		// These land on `<v-icon :color>`, a FOREGROUND use, so the rework entry
		// takes the -ink token: the base #c2410c is the fill value, and ink is the
		// one guaranteed >=4.5:1 on white and on its own tint.
		getEventColor(eventType) {
			const colors = {
				rework_request: WORKFLOW_STATES.rework.ink,
				comment_reply: "secondary",
				comment_directed: "info",
				rework_resubmitted: "success",
				comment_resolved: "success",
				// Attention, not rework: something happened TO this record that
				// the reviewer must look at. -ink because this lands on
				// `<v-icon :color>`, a foreground use.
				qc_reopened: "valence-attention-ink",
				input_reopened: "valence-attention-ink",
			};
			return colors[eventType] || "grey-darken-2";
		},

		getEventDescription(notification) {
			const descriptions = {
				rework_request: "flagged your extraction for rework",
				comment_reply: "replied to your comment",
				comment_directed:
					"left a comment on your extraction (not a rework request)",
				rework_resubmitted: "resubmitted their extraction",
				comment_resolved: "resolved your comment",
				qc_reopened: "reopened this QC for a data fix",
				input_reopened: "reopened an extraction under your QC",
			};
			return descriptions[notification.eventType] || "sent you a notification";
		},

		formatTime(dateStr) {
			if (!dateStr) return "";
			const date = new Date(dateStr);
			const now = new Date();
			const diffMs = now - date;
			const diffMin = Math.floor(diffMs / 60000);
			if (diffMin < 1) return "now";
			if (diffMin < 60) return `${diffMin}m ago`;
			const diffHr = Math.floor(diffMin / 60);
			if (diffHr < 24) return `${diffHr}h ago`;
			const diffDays = Math.floor(diffHr / 24);
			if (diffDays < 7) return `${diffDays}d ago`;
			return date.toLocaleDateString();
		},
	},
};
</script>

<style scoped>
.notification-bell-btn {
	min-width: 36px !important;
	width: 36px;
	padding: 0 !important;
}

.notification-bell-btn :deep(.v-badge__badge) {
	min-width: 18px;
	height: 18px;
	font-size: 10px;
}

.notification-item {
	border-bottom: 1px solid var(--border-default);
}

.notification-item:last-child {
	border-bottom: none;
}

.notification-item:hover {
	background: rgba(0, 0, 0, 0.03);
}

.unread-item {
	background: rgba(52, 123, 153, 0.06);
}

.cursor-pointer {
	cursor: pointer;
}

.border-b {
	border-bottom: 1px solid var(--border-default);
}
</style>
